import type { AbaRecebimentosCentral, EstadoSincronizacaoRecebimentos, RecebimentoCentralDetalhe, RecebimentoCentralResumo } from '@pieltec/contracts';

export const ABAS_RECEBIMENTOS: readonly { readonly id: AbaRecebimentosCentral; readonly url: string; readonly rotulo: string }[] = [
  { id: 'A_CONFERIR', url: 'conferir', rotulo: 'A conferir' },
  { id: 'A_CLASSIFICAR', url: 'classificar', rotulo: 'A classificar' },
  { id: 'COM_PENDENCIAS', url: 'pendencias', rotulo: 'Com pendências' },
  { id: 'PROCESSADAS', url: 'processadas', rotulo: 'Processadas' },
];

export interface FiltrosRecebimentos {
  readonly aba: AbaRecebimentosCentral;
  readonly pagina: number;
  readonly limite: number;
  readonly busca: string;
}

export const LIMITE_RECEBIMENTOS_PADRAO = 10;
const LIMITES_RECEBIMENTOS: readonly number[] = [10, 30, 50, 100];

export interface TextoSincronizacao {
  readonly status: string;
  readonly erro: string | null;
}

export interface ItemConferenciaRecebimento {
  readonly id: string;
  readonly numeroItem: number;
  readonly descricao: string;
  readonly unidadeFiscal: string;
  readonly quantidadeDocumental: number;
  /** Total físico já consolidado; nunca é a sugestão de um novo recebimento. */
  readonly quantidadeRecebida: number;
  readonly quantidadeFisica: number | null;
  readonly codigoItemCliente: string | null;
  readonly pedidoVendaItemId: string | null;
  readonly justificativaExcedente: string;
}

export interface EstadoConferenciaRecebimento {
  readonly recebimentoId: string;
  readonly versao: string;
  readonly classificacaoCaptura: RecebimentoCentralDetalhe['classificacaoCaptura'];
  readonly canceladoEm: string | null;
  readonly itens: readonly ItemConferenciaRecebimento[];
  /** Uma tentativa deliberada preserva a chave em todos os retries; uma nova confirmação chama o inicializador de novo. */
  readonly idempotencyKey: string;
}

export interface PayloadConfirmacaoFisica {
  readonly itens: readonly {
    readonly documentoFiscalItemId: string;
    readonly quantidadeFisica: number;
    readonly pedidoVendaItemId?: string;
    readonly justificativaExcedente?: string;
  }[];
  readonly idempotencyKey: string;
}

export function abaCanonicaRecebimento(valor: string | null | undefined): AbaRecebimentosCentral {
  return ABAS_RECEBIMENTOS.find((aba) => aba.url === valor || aba.id === valor)?.id ?? 'A_CONFERIR';
}

export function lerFiltrosRecebimentos(search: string): FiltrosRecebimentos {
  const parametros = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const paginaLida = Number(parametros.get('pagina'));
  const limiteLido = Number(parametros.get('limite'));
  return {
    aba: abaCanonicaRecebimento(parametros.get('aba')),
    pagina: Number.isInteger(paginaLida) && paginaLida > 0 ? paginaLida : 1,
    limite: LIMITES_RECEBIMENTOS.includes(limiteLido) ? limiteLido : LIMITE_RECEBIMENTOS_PADRAO,
    busca: parametros.get('busca')?.trim() ?? '',
  };
}

/** URL é a fonte da lista: voltar restaura aba, busca, página e tamanho, sem estado oculto. */
export function escreverFiltrosRecebimentos(filtros: FiltrosRecebimentos): string {
  const parametros = new URLSearchParams();
  const aba = ABAS_RECEBIMENTOS.find((candidata) => candidata.id === filtros.aba);
  if (aba && aba.id !== 'A_CONFERIR') parametros.set('aba', aba.url);
  if (filtros.pagina > 1) parametros.set('pagina', String(filtros.pagina));
  if (LIMITES_RECEBIMENTOS.includes(filtros.limite) && filtros.limite !== LIMITE_RECEBIMENTOS_PADRAO) parametros.set('limite', String(filtros.limite));
  if (filtros.busca) parametros.set('busca', filtros.busca);
  return parametros.toString();
}

/** Menor número vem primeiro: cancelamento e pendência nunca desaparecem em uma aba menos crítica. */
export function prioridadeRecebimento(recebimento: Pick<RecebimentoCentralResumo, 'aba' | 'canceladoEm' | 'classificacaoCaptura'>): number {
  if (recebimento.canceladoEm !== null || recebimento.aba === 'COM_PENDENCIAS') return 0;
  if (recebimento.classificacaoCaptura === 'A_CLASSIFICAR' || recebimento.aba === 'A_CLASSIFICAR') return 1;
  if (recebimento.aba === 'A_CONFERIR') return 2;
  return 3;
}

export function ordenarRecebimentos(recebimentos: readonly RecebimentoCentralResumo[]): readonly RecebimentoCentralResumo[] {
  return [...recebimentos].sort((esquerda, direita) => prioridadeRecebimento(esquerda) - prioridadeRecebimento(direita));
}

export function textoSincronizacao(sincronizacao: EstadoSincronizacaoRecebimentos): TextoSincronizacao {
  const ultimoSucesso = sincronizacao.ultimoSucessoEm ? new Date(sincronizacao.ultimoSucessoEm).toLocaleString('pt-BR') : 'ainda não houve sincronização concluída';
  return {
    status: `Última sincronização bem-sucedida: ${ultimoSucesso}.`,
    erro: sincronizacao.ultimoErroEm ? (sincronizacao.ultimoErroMensagem?.trim() || 'A última atualização não foi concluída. Os dados já carregados foram preservados.') : null,
  };
}

export function criarChaveConfirmacaoFisica(): string {
  return globalThis.crypto?.randomUUID?.() ?? `recebimento-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function estadoInicialConferenciaRecebimento(detalhe: RecebimentoCentralDetalhe, idempotencyKey = criarChaveConfirmacaoFisica()): EstadoConferenciaRecebimento {
  return {
    recebimentoId: detalhe.id,
    versao: detalhe.versao,
    classificacaoCaptura: detalhe.classificacaoCaptura,
    canceladoEm: detalhe.canceladoEm,
    idempotencyKey,
    itens: detalhe.itens.map((item) => ({
      id: item.id,
      numeroItem: item.numeroItem,
      descricao: item.descricao,
      unidadeFiscal: item.unidadeFiscal,
      quantidadeDocumental: item.quantidadeDocumental,
      quantidadeRecebida: item.quantidadeRecebida,
      // Cada confirmação representa uma nova entrada física. Reabrir a nota não
      // pode repetir o total anteriormente registrado com uma chave nova.
      quantidadeFisica: null,
      codigoItemCliente: item.codigoItemCliente,
      pedidoVendaItemId: null,
      justificativaExcedente: '',
    })),
  };
}

export function atualizarItemConferencia(
  estado: EstadoConferenciaRecebimento,
  itemId: string,
  atualizacao: Partial<Omit<ItemConferenciaRecebimento, 'id' | 'numeroItem'>>,
): EstadoConferenciaRecebimento {
  return { ...estado, itens: estado.itens.map((item) => (item.id === itemId ? { ...item, ...atualizacao } : item)) };
}

/** Desconhecido e divergente vêm antes; ordem documental é o desempate estável. */
export function ordenarItensDaConferencia(itens: readonly ItemConferenciaRecebimento[]): readonly ItemConferenciaRecebimento[] {
  return [...itens].sort((esquerda, direita) => {
    const criticaEsquerda = esquerda.codigoItemCliente === null || esquerda.quantidadeFisica === null || esquerda.quantidadeFisica !== esquerda.quantidadeDocumental ? 0 : 1;
    const criticaDireita = direita.codigoItemCliente === null || direita.quantidadeFisica === null || direita.quantidadeFisica !== direita.quantidadeDocumental ? 0 : 1;
    return criticaEsquerda - criticaDireita || esquerda.numeroItem - direita.numeroItem;
  });
}

export function motivoBloqueioConfirmacao(estado: EstadoConferenciaRecebimento): string | null {
  if (estado.canceladoEm !== null) return 'Esta nota foi cancelada e não pode ser recebida fisicamente.';
  if (estado.classificacaoCaptura !== 'INDUSTRIAL') return 'Somente remessas industriais classificadas podem confirmar recebimento físico.';
  if (estado.itens.some((item) => item.codigoItemCliente === null)) return 'Há item sem identificação. Classifique, cadastre ou vincule o item antes da confirmação.';
  if (estado.itens.every((item) => item.quantidadeFisica === null || item.quantidadeFisica <= 0)) return 'Informe ao menos uma quantidade física para confirmar o recebimento.';
  if (estado.itens.some((item) => item.quantidadeFisica !== null && item.quantidadeFisica > item.quantidadeDocumental && item.justificativaExcedente.trim().length < 5)) return 'Informe a justificativa para cada quantidade acima da nota.';
  return null;
}

/** A única projeção para o POST físico; todas as demais alterações ficam apenas no estado React. */
export function montarPayloadConfirmacaoFisica(estado: EstadoConferenciaRecebimento): PayloadConfirmacaoFisica {
  return {
    idempotencyKey: estado.idempotencyKey,
    itens: estado.itens.flatMap((item) => {
      if (item.quantidadeFisica === null || item.quantidadeFisica <= 0) return [];
      return [{
        documentoFiscalItemId: item.id,
        quantidadeFisica: item.quantidadeFisica,
        ...(item.pedidoVendaItemId ? { pedidoVendaItemId: item.pedidoVendaItemId } : {}),
        ...(item.justificativaExcedente.trim() ? { justificativaExcedente: item.justificativaExcedente.trim() } : {}),
      }];
    }),
  };
}

export interface AcoesRecebimento {
  readonly podeLer: boolean;
  readonly podeAtualizar: boolean;
  readonly podeConsultarXml: boolean;
  readonly podeResolverPendencia: boolean;
  readonly podeConfirmarFisico: boolean;
}

/** A UI apenas esconde affordances; a permissão efetiva continua sendo conferida pelo backend. */
export function acoesRecebimento(permissoes: readonly string[]): AcoesRecebimento {
  const tem = (permissao: string): boolean => permissoes.includes(permissao);
  return {
    podeLer: tem('recebimentos:consultar'),
    podeAtualizar: tem('recebimentos:atualizar'),
    // O XML continua na rota fiscal já protegida por permissão dedicada, nunca na lista da central.
    podeConsultarXml: tem('remessas:ver-fiscal-completo'),
    podeResolverPendencia: tem('recebimentos:resolver'),
    podeConfirmarFisico: tem('remessas:receber'),
  };
}
